import { HttpError } from 'react-admin';
import { fileToBase64 } from './base64/fileToBase64';
import { LOCALE_NAMES, NEWS_LOCALES, SOURCE_LOCALE } from '../../components/pages/news_posts/common';

/**
 * Novedades (86c52b7mu): adaptación entre el registro de react-admin y el contrato de
 * `/api/admin/news-posts`.
 *
 * La imagen se edita en el campo de formulario `image` (`{ src, title }` con la imagen guardada,
 * `{ rawFile, src, title }` con una nueva, `null` si se quita) y viaja al back como `imageBase64`,
 * que tiene tres significados: ausente = no se toca, `null` = se quita, data URL = se sustituye.
 * Por eso no basta con `handleImageBase64Conversion`, que nunca manda `null`.
 */
export const withNewsPostImage = (record) => ({
    ...record,
    image: record.image_url ? { src: record.image_url, title: 'Imagen actual' } : null,
});

const READ_ONLY_FIELDS = ['image', 'image_url', 'status', 'metrics', 'translation_status', 'created_at', 'updated_at'];

export const buildNewsPostPayload = async (params, { isCreate }) => {
    const data = { ...params.data };
    const image = data.image;
    READ_ONLY_FIELDS.forEach((field) => delete data[field]);

    if (image?.rawFile instanceof File) {
        data.imageBase64 = await fileToBase64(image.rawFile);
    } else if (!image && params.previousData?.image_url) {
        data.imageBase64 = null;
    }

    if (isCreate) {
        // En el alta, un idioma vacío significa «tradúcelo»: no se manda. En la edición sí se
        // manda vacío, que para el back es «vuelve a la traducción automática».
        ['title', 'body'].forEach((field) => {
            const values = { ...(data[field] || {}) };
            NEWS_LOCALES.forEach((locale) => {
                if (locale !== SOURCE_LOCALE && !values[locale]) delete values[locale];
            });
            data[field] = values;
        });
    }

    return { ...params, data };
};

const FIELD_NAMES = {
    title: 'Título',
    body: 'Texto',
    starts_at: 'Fecha de inicio',
    ends_at: 'Fecha de fin',
    imageBase64: 'Imagen',
};

// `imageBase64` es el nombre en el contrato; en el formulario la imagen vive en `image`.
const FORM_FIELDS = { imageBase64: 'image' };

/**
 * Traduce un 400 de validación del back (`{"message": "Validation errors", "errors":
 * {"[title][es]": ["…"], "[ends_at]": ["…"]}}`, claves en formato propertyPath de Symfony) a:
 * - `message`: la lista legible de errores, una línea por campo con su idioma, para el `notify`;
 * - `body.errors`: los mismos mensajes anidados por campo de formulario (`{title: {es: "…"}}`),
 *   que react-admin pinta en cada input al fallar el guardado.
 * Cualquier otro error pasa tal cual.
 */
export const toNewsPostValidationError = (error) => {
    const errors = error?.body?.errors;
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return error;

    const lines = [];
    const formErrors = {};
    Object.entries(errors).forEach(([path, messages]) => {
        const text = [].concat(messages).join(' ');
        const [field, locale] = (path.match(/[^[\]]+/g) || [path]);
        const name = FIELD_NAMES[field] || field;
        lines.push(`• ${name}${locale ? ` (${LOCALE_NAMES[locale] || locale})` : ''}: ${text}`);

        const formField = FORM_FIELDS[field] || field;
        if (locale) {
            formErrors[formField] = { ...(formErrors[formField] || {}), [locale]: text };
        } else {
            formErrors[formField] = text;
        }
    });

    // El back ya manda `message` en castellano, pero la lista se construye desde `errors` para
    // poder nombrar el campo y el idioma de cada uno.
    const message = ['Revisa estos campos:', ...lines].join('\n');
    return new HttpError(message, error.status, { ...error.body, errors: formErrors });
};
